import moment, { Moment } from "moment";
import { PayHelper } from "../scCommon/scCommonFunc";
import { GetMoneyValue } from "./ApiCommonTS";
import { IPartyData } from "./CommonType";
import {
  GetRewardSetData,
  GetSubscriptionSetData,
  GetSubsDataOnlyActivateByServiceId,
} from "./ServiceDatasTS";

// 날짜 간격 구하기
export const getDiffDay = (
  fromDate: string | Moment,
  ToDate: string | Moment
) => {
  const formatedFromDate = moment(fromDate);
  const formatedToDate = moment(ToDate);

  return moment(formatedToDate).diff(formatedFromDate, "days");
};

// 파티 약정 기간(월) 구하기
export const getPartyContractPeriod = (
  partyStartDate: string | Moment,
  partyEndDate: string | Moment
) => {
  const formatedStartDate = moment(partyStartDate);
  const formatedEndDate = moment(partyEndDate);

  return formatedEndDate.diff(formatedStartDate, "months") ?? 0;
};

// 파티원 요금
export const getPartyMemberFee = {
  // subsId별 링키드 수수료 (파티원)
  subs_mbrComm: (subsId: number) => {
    const subsData = GetSubscriptionSetData(subsId);

    const linkidComm = subsData
      ? PayHelper?.calc_commission(subsData, subsData?.SUBS_FEE)
      : 0;

    return linkidComm;
  },
  // subsId별 월 이용 요금
  subs_discMonthFee: (subsId: number) => {
    const subsData = GetSubscriptionSetData(subsId);
    const linkidComm = subsData
      ? PayHelper?.calc_commission(subsData, subsData?.SUBS_FEE)
      : 0;

    return (subsData?.SUBS_FEE ?? 0) + linkidComm;
  },
  // subsId별 파티 보증금
  subs_partyGuaranteeFee: (
    subsId: number,
    startdate: string,
    enddate: string,
    joindate?: string
  ) => {
    const subsData = GetSubscriptionSetData(subsId);
    const guaranteeFee = subsData
      ? PayHelper.guarantee(
          subsData,
          startdate,
          enddate,
          joindate ?? moment().format("YYYY-MM-DD")
        )
      : 0;

    return guaranteeFee;
  },
  // 파티 분담금
  subs_fee: (subsId: number) => {
    const subsData = GetSubscriptionSetData(subsId);
    const value = parseInt((subsData?.SUBS_FEE ?? 0) + "");

    return value;
  },
  // 서비스 별 할인 전 월 이용 요금 (최소 값)
  serv_originMonthFee: (servId: number) => {
    const subsDataByServId = GetSubsDataOnlyActivateByServiceId(servId);
    let value = 999999;

    subsDataByServId?.forEach((s) => {
      if (s.SUBS_FEE * s.POL_SHARMX < value) {
        value = s.SUBS_FEE * s.POL_SHARMX;
      }
    });

    return `${GetMoneyValue(value)}${subsDataByServId?.length > 1 && "~"}`;
  },
  // 서비스 별 할인 후 월 이용 요금 (최소 값)
  serv_discMonthFee: (servId: number) => {
    const subsDataByServId = GetSubsDataOnlyActivateByServiceId(servId);
    let value = 999999;

    subsDataByServId?.forEach((s) => {
      const comm = PayHelper.calc_commission(s, s.SUBS_FEE);

      if (s?.SUBS_FEE + comm < value) {
        value = s?.SUBS_FEE + comm;
      }
    });

    return `${GetMoneyValue(value)}${subsDataByServId?.length > 1 && "~"}`;
  },
  // 처음 파티 시작일에 결제될 금액
  firstPartyFee: (
    subsId: number,
    startDate: string,
    endDate: string,
    joinDate: string,
    payday: number,
    serverDeposit?: number
  ) => {
    const subsData = GetSubscriptionSetData(subsId);

    // 파티가 이미 시작했는지
    const hasPartyStarted = !Boolean(getDiffDay(startDate, joinDate) < 1);

    // 파티원이 실제로 파티를 시작하는 날
    const memberPartyStartDate = hasPartyStarted ? joinDate : startDate;

    // 다음 정산일
    const nextPayday = PayHelper.nextpdate(
      payday,
      moment(memberPartyStartDate)
    );

    const nowFeeTotal = PayHelper.allotment(
      subsData,
      memberPartyStartDate,
      nextPayday,
      endDate,
      nextPayday
    ).total;

    // 보증금
    const guaranteeFee = hasPartyStarted
      ? getPartyMemberFee.subs_partyGuaranteeFee(
          subsId,
          startDate,
          endDate,
          joinDate
        )
      : serverDeposit ?? 0;

    return guaranteeFee + nowFeeTotal;
  },
};

// 파티장 요금
export const getPartyOwnerFee = {
  // 서비스 별 매달 최대 적립 금액
  serv_maxSaveFee: (servId: number) => {
    const subsDataByServId = GetSubsDataOnlyActivateByServiceId(servId);
    let value = 0;

    subsDataByServId?.forEach((s) => {
      if (getMaxSavedMonthlyMoneyBySubs(s?.SUBS_ID ?? 0, true) > value) {
        value = getMaxSavedMonthlyMoneyBySubs(s?.SUBS_ID ?? 0, true);
      }
    });

    return value;
  },
  // subsId 별 파티원 1명당 매달 적립 받는 금액
  subs_mbrFee: (subsId: number) => {
    const subsData = GetSubscriptionSetData(subsId);
    const linkidComm = (subsData?.COMM_LDRRT ?? 0) / 10000;

    return (
      Math.round(
        ((subsData?.SUBS_FEE ?? 0) - (subsData?.SUBS_FEE ?? 0) * linkidComm) / 5
      ) * 5
    );
  },
  // subsId 별 최대 인원 모집시 매달 적립 받는 금액
  subs_maxMbrFee: (subsId: number) => {
    const subsData = GetSubscriptionSetData(subsId);

    return (
      Math.round(
        (((subsData?.SUBS_FEE ?? 0) -
          (subsData?.SUBS_FEE ?? 0) * ((subsData?.COMM_LDRRT ?? 0) / 10000)) *
          ((subsData?.POL_SHARMX ?? 0) - 1)) /
          5
      ) * 5
    );
  },
  // subsId 별 파티원 1명당 할인 되는 링키드 수수료 * MAX 파티원
  subs_ldrComm: (subsId: number) => {
    const subsData = GetSubscriptionSetData(subsId);
    const linkidComm = (subsData?.COMM_LDRRT ?? 0) / 10000;

    return (
      Math.round(
        (((subsData?.SUBS_FEE ?? 0) - (subsData?.SUBS_FEE ?? 0) * linkidComm) *
          ((subsData?.POL_SHARMX ?? 0) - 1)) /
          5
      ) * 5
    );
  },
  // subsId 별 파티원 1명당 할인 되는 링키드 수수료
  subs_ldrCommByMember: (subsId: number) => {
    const subsData = GetSubscriptionSetData(subsId);
    const linkidComm = (subsData?.COMM_LDRRT ?? 0) / 10000;

    return Math.round(((subsData?.SUBS_FEE ?? 0) * linkidComm) / 5) * 5;
  },
  // 파티장 최대 위약금
  ldr_penalty: (
    subsId: number,
    startdate: string,
    enddate: string,
    partyMemberCnt: number,
    joindate?: string
  ) => {
    const subsData = GetSubscriptionSetData(subsId);

    const ldrGuarantee = PayHelper.guarantee_owner(
      subsData,
      startdate,
      enddate,
      joindate ?? moment().format("YYYY-MM-DD")
    );

    return parseInt(`${ldrGuarantee * partyMemberCnt}`);
  },
};

// 파티 생성/ 파티 가입 시 최대 리워드 값을 계산한다.
export const GetRewCalcValue = (
  isHost: boolean,
  memcnt: number,
  subsid: number,
  startdate?: string,
  enddate?: string,
  joindate?: string,
  shareTm?: string
) => {
  const subsData = GetSubscriptionSetData(subsid);
  const rwdData: any = GetRewardSetData(subsData?.REW_ID ?? 0);

  console.log(rwdData);

  // 남은 결제일 횟수를 구한다.
  let mPstart = moment(startdate);
  const mEnd = moment(enddate);
  const mJoin = moment(joindate);

  if (mPstart < mJoin) mPstart = mJoin;

  let retCount = 0;

  const period = getPartyContractPeriod(mPstart, mEnd) ?? 0;

  if (shareTm === "day") {
    retCount = moment.duration(period).asDays();
  } else if (shareTm === "week") {
    retCount = moment.duration(period).asWeeks();
  } else if (shareTm === "month") {
    retCount = moment.duration(period).asMonths();
  } else {
    retCount = moment.duration(period).asYears();
  }

  const retFinal = PayHelper.get_maxrewardstep_compare_inputperiod(
    rwdData,
    retCount
  );

  let value = 0;
  if (isHost) {
    value = parseInt(
      `${
        subsData?.SUBS_FEE ??
        0 *
          memcnt *
          (subsData?.COMM_LDRRT ?? 0 / 10000) *
          (rwdData?.retFinal ?? 0 / 10000) *
          retFinal
      }`
    );
  } else {
    value = parseInt(
      `${
        subsData?.SUBS_FEE ??
        0 *
          (subsData?.COMM_MBRRT ?? 0 / 10000) *
          (rwdData?.retFinal / 10000) *
          retFinal
      }`
    );
  }

  if (isNaN(value)) {
    value = 0;
  }

  return value;
};

// 남은 기간동안 받을수 있는 리워드 값을 계산한다.
export const GetFutureRewCalcValue = (
  isHost: boolean,
  memcnt: number,
  subsid: number,
  _type: string,
  payday: number,
  startdate: string,
  enddate: string,
  joindate: string
) => {
  const subsData = GetSubscriptionSetData(subsid);

  if (!subsData) return 0;

  const rwdData: any = GetRewardSetData(subsData?.REW_ID ?? 0);

  console.log(rwdData);

  const today = moment(moment().format("YYYY-MM-DD"));
  let payDay = moment(moment().format("YYYY-MM-") + payday, "YYYY-MM-DD");
  const nextPday = PayHelper.nextpdate(payday); // 다음 결제일

  let sPday;
  if (payDay <= today) {
    sPday = nextPday;
  } else {
    sPday = today;
  }

  // 시작일과 (파티원이면 가입일) 종료일 간의 개월수를 구한다.
  let mstart = moment(startdate);
  const mjoin = moment(joindate);

  // 남은 결제일 횟수를 구한다.
  let mPstart = moment(sPday);
  const mEnd = moment(enddate);

  if (mstart < mjoin) mstart = mjoin;

  let retCountS = 0;
  const period2 = getPartyContractPeriod(mstart, mEnd) ?? 0;

  if (_type == "day") {
    retCountS = moment.duration(period2).asDays();
  } else if (_type == "week") {
    retCountS = moment.duration(period2).asWeeks();
  } else if (_type == "month") {
    retCountS = moment.duration(period2).asMonths();
  } else {
    retCountS = moment.duration(period2).asYears();
  }

  const retFinal = parseInt(
    `${PayHelper.get_maxrewardstep_compare_inputperiod(rwdData, retCountS)}`
  );

  let value = 0;
  let retCount = 0;
  const period = getPartyContractPeriod(mPstart, mEnd) ?? 0;

  if (_type === "day") {
    retCount = moment.duration(period).asDays();
  } else if (_type === "week") {
    retCount = moment.duration(period).asWeeks();
  } else if (_type === "month") {
    retCount = moment.duration(period).asMonths();
  } else {
    retCount = moment.duration(period).asYears();
  }

  // 마지막달은 1로 보정.
  if (retCount < 1) {
    retCount = 1;
  }
  retCount = parseInt(`${retCount}`);

  // 최종 값을 구한다.
  if (isHost) {
    value = parseInt(
      `${
        (subsData?.SUBS_FEE ?? 0) *
        (memcnt ?? 0) *
        ((subsData?.COMM_LDRRT ?? 0) / 10000) *
        ((rwdData?.retFinal ?? 0) / 10000) *
        retCount
      }`
    );
  } else {
    value = parseInt(
      `${
        (subsData?.SUBS_FEE ?? 0) *
        ((subsData?.COMM_MBRRT ?? 0) / 10000) *
        ((rwdData?.retFinal ?? 0) / 10000) *
        retCount
      }`
    );
  }

  return value;
};

// 구독별 월 최대 세이브 머니 계산.
export const getMaxSavedMonthlyMoneyBySubs = (
  subsID: number,
  isHost: boolean
) => {
  const subsData = GetSubscriptionSetData(subsID);

  let value = 0;

  if (subsData) {
    let comm = isHost
      ? (subsData?.COMM_LDRRT ?? 0) / 10000
      : (subsData?.COMM_MBRRT ?? 0) / 10000;
    comm = isHost
      ? comm * subsData?.SUBS_FEE * (subsData?.POL_SHARMX - 1)
      : comm * subsData?.SUBS_FEE;

    value =
      Math.round(((subsData.POL_SHARMX - 1) * subsData.SUBS_FEE - comm) / 5) *
      5;
    // value = parseInt((subsData.POL_SHARMX - 1) * subsData.SUBS_FEE - comm);
  }
  return value;
};

// 월 내가 내야하는 요금이 가장 적은 구독권의 값을 계산(subsfee + comm)
export const getMinPerMonthFeeJustMeWithCommValueOnSubsLists = (
  scvId: number
) => {
  const SubsList = GetSubsDataOnlyActivateByServiceId(scvId);

  let value = 999999;
  let moneyValue = "";

  for (let i = 0; i < SubsList.length; i++) {
    const comm = PayHelper.calc_commission(SubsList[i], SubsList[i].SUBS_FEE);
    if (SubsList[i].SUBS_FEE + comm < value) {
      value = SubsList[i].SUBS_FEE + comm;
    }
  }
  moneyValue = GetMoneyValue(value);
  if (SubsList.length > 1) {
    moneyValue = moneyValue + "~";
  }
  return moneyValue;
};

// 총 얼마를 아끼고 있는지.
export const GetSavingMoneyTotal = (partyList: IPartyData[]) => {
  let value = 0;
  for (let i = 0; i < partyList.length; i++) {
    const data = partyList[i];
    const SubsData = GetSubscriptionSetData(data.servsubsid);

    if (SubsData !== undefined) {
      const sDate = data.grade === 0 ? data.startdate : data.joindate;
      const period = PayHelper.calc_period(
        SubsData.SUBS_SHARTM,
        data.startdate,
        data.enddate,
        sDate
      );

      const maxSaveForAll =
        getMaxSavedMonthlyMoneyBySubs(
          data.servsubsid,
          data.grade === 0 ? true : false
        ) * period;

      value = value + maxSaveForAll;
    }
  }
  return value;
};

// 월 단위로 얼마나 아끼고 있는지.
export const GetSavingMoneyPerMonth = (partyList: IPartyData[]) => {
  let value = 0;

  for (let i = 0; i < partyList.length; i++) {
    const data = partyList[i];
    const subsData = GetSubscriptionSetData(data.servsubsid);

    if (subsData) {
      const maxSavePerMonth = getMaxSavedMonthlyMoneyBySubs(
        data.servsubsid,
        data.grade === 0 ? true : false
      );

      value = value + maxSavePerMonth;
    }
  }
  return value;
};
